import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { Observable, Subscription, filter, interval, map } from 'rxjs';
import { Device, supportTasks } from 'src/app/model/device';
import { DeviceModel } from 'src/app/model/device-model';
import { DeviceStatus, isAutomFwUpdateInProgress, isDeviceUnavailable } from 'src/app/model/device-status';
import { selectCurrentDevice, selectCurrentDeviceFeatures, selectCurrentDeviceInfo, selectCurrentDeviceModel, selectCurrentDeviceStatus, selectMtmaActiveTask, selectMtmaChanges, selectMtmaModules } from 'src/app/store/device/device.selector';
import { DeviceInfo } from 'src/app/model/device-info';
import { Site } from 'src/app/model/site';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { selectActiveTask } from 'src/app/store/task/task.selector';
import { Task } from 'src/app/model/tasks';
import { ETaskStatus } from 'src/app/conf/tasks';
import { selectIsImpersMode, selectRoleExplainedTechnician } from 'src/app/store/user/user.selector';
import { TasksPollingService } from 'src/app/services/task-polling.service';
import { EDeviceCategory } from 'src/app/conf/device-categories';
import { DevicesTree } from 'src/app/model/device-tree';
import { EDeviceModel, isMtmacModel, isMtmacStandardModel, isThOneGw } from 'src/app/conf/device-models';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { MtmaModule } from 'src/app/model/mtma-module';
import { MtmaTask } from 'src/app/model/mtma-task';
import { CLOUD_FW_UPGRADE_STATE } from 'src/app/conf/firmware-upgrade';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TasksDialogComponent } from '../tasks-dialog/tasks-dialog.component';
import * as SiteActions from 'src/app/store/site/site.actions';
import { EditDeviceDialogComponent } from '../edit-device-dialog/edit-device.dialog.component';
import { UpdateFirmwareDialogComponent } from '../update-firmware-dialog/update-firmware.dialog.component';
import { ShareDeviceDialogComponent } from '../share-device-dialog/share-device.dialog.component';
import { StandardDesignParameters, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { PasswordEditDialogComponent } from '../password-edit-dialog/password-edit.dialog.component';
import { DeviceFeature } from 'src/app/model/device-feature';
import { AddAutomationDialogComponent } from '../add-automation-dialog/add-automation.dialog.component';
import * as DeviceVctActions from 'src/app/store/device-vct/device-vct.actions';
import * as InvitationsActions from 'src/app/store/invitations/invitations.actions';

@Component({
	selector: 'app-device-button-bar',
	templateUrl: './device-button-bar.component.html',
	providers: [ConfirmationService, DialogService]
})

export class DeviceButtonBarComponent implements OnInit, OnDestroy {
	device$: Observable<Device | undefined>;
	deviceSub: Subscription | undefined;
	deviceModel$: Observable<DeviceModel | undefined>;
	deviceModelSub: Subscription | undefined;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;
	deviceInfo$: Observable<DeviceInfo | undefined>;
	deviceInfoSub: Subscription | undefined;
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	tasks$: Observable<Task[] | undefined>;
	tasksSub: Subscription | undefined;
	impersMode$: Observable<boolean | undefined>;
	impersModeSub: Subscription | undefined;
	mtmaModules$: Observable<MtmaModule[] | undefined>;
	mtmaModulesSub: Subscription | undefined;
	mtmaChanges$: Observable<boolean | undefined>;
	mtmaChangesSub: Subscription | undefined;
	mtmaActiveTask$: Observable<MtmaTask | undefined>;
	mtmaActiveTaskSub: Subscription | undefined;

	deviceFeature$: Observable<DeviceFeature[] | undefined>;
	deviceFeatureSub: Subscription | undefined;
	isTechnician$: Observable<boolean | undefined>;
	isTechnicianSub: Subscription | undefined;

	passwordSupport: boolean;
	passwordVerify: boolean;
	isTechnician: boolean;

	polling: Subscription;

	device: Device;
	deviceModel: DeviceModel;
	deviceStatus: DeviceStatus;
	deviceInfo: DeviceInfo;
	iconName: string;
	runningTasksCount: number;
	impersMode: boolean;
	currentSite: Site;

	childAutomationsCount: number;
	childSubGatewaysCount: number;
	progress: string = undefined;

	mtmaActiveTask: MtmaTask;
	mtmAc: MtmaModule;
	mtmaChanges: boolean;
	listOtherMtma: DevicesTree[];
	menuitems: MenuItem[] = [];
	ref: DynamicDialogRef | undefined;
	shareDeleteSub: Subscription;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	screenWidth: number;

	minLimitVersion = [1, 1, 8];

	constructor(
		private store: Store<AppState>,
		private taskPollingService: TasksPollingService,
		private router: Router,
		private route: ActivatedRoute,
		private translate: TranslateService,
		public dialogService: DialogService,
		public confirmationService: ConfirmationService,
	) {
		this.initSelectors();
	}

	ngOnInit(): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.subscribeAll();
		this.onResize();
	}

	initSelectors(): void {
		// Store selectors
		this.device$ = this.store.select(selectCurrentDevice);
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.deviceModel$ = this.store.select(selectCurrentDeviceModel);
		this.deviceInfo$ = this.store.select(selectCurrentDeviceInfo);
		this.site$ = this.store.select(selectCurrentSite);
		this.tasks$ = this.store.select(selectActiveTask);
		this.impersMode$ = this.store.select(selectIsImpersMode);
		this.mtmaModules$ = this.store.select(selectMtmaModules);
		this.mtmaChanges$ = this.store.select(selectMtmaChanges);
		this.mtmaActiveTask$ = this.store.select(selectMtmaActiveTask);
		this.deviceFeature$ = this.store.select(selectCurrentDeviceFeatures);
		this.isTechnician$ = this.store.select(selectRoleExplainedTechnician);
	}

	subscribeAll(): void {
		this.deviceSub = this.device$.pipe(filter(d => !!d))
			.subscribe(currentDevice => {
				this.device = currentDevice;
				this.deviceStatus = new DeviceStatus(this.device.Id);
				/*if (this.hasTasks()) { // REMOVE TASK
					this.taskPollingService.startPolling(this.device);
				}*/
				this.updateMenu();
			});
		this.deviceStatusSub = this.deviceStatus$.pipe(filter(d => !!d))
			.subscribe(currentDeviceStatus => {
				this.deviceStatus = currentDeviceStatus;
				this.updateMenu();
			});
		this.deviceModelSub = this.deviceModel$.pipe(filter(d => !!d))
			.subscribe(currentDeviceModel => {
				this.deviceModel = currentDeviceModel;
				this.updateMenu();
			});
		this.deviceInfoSub = this.deviceInfo$.pipe(filter(d => !!d))
			.subscribe(currentDeviceInfo => {
				this.deviceInfo = currentDeviceInfo;
				this.updateMenu();
			});

		this.siteSub = this.site$.pipe(filter(site => !!site))
			.subscribe(currentSite => {
				this.currentSite = currentSite;
				const devices = currentSite.Devices;
				this.childAutomationsCount = devices.filter(
					d => d.ParentId === this.device.Id && d.CategoryId === EDeviceCategory.Automation
				).length;
				this.childSubGatewaysCount = devices.filter(
					d => d.ParentId === this.device.Id && d.CategoryId === EDeviceCategory.SubGateway
				).length;
				this.listOtherMtma = this.currentSite.DevicesTree.Children.filter(device => isMtmacModel(device.ModelId) && device.Id !== this.device.Id && device.Children.length === 0);
				this.updateMenu();
			});
		this.tasksSub = this.tasks$.pipe(
			map(tasks => tasks.filter(task => task.Status === ETaskStatus.RUNNING || task.Status === ETaskStatus.PENDING)),
			map(tasks => tasks.length)
		).subscribe(runningTasksCount => {
			this.runningTasksCount = runningTasksCount;
			this.updateMenu();
		});
		this.impersModeSub = this.impersMode$.subscribe(impersMode => {
			this.impersMode = impersMode;
			this.updateMenu();
		});
		this.deviceFeatureSub = this.deviceFeature$.subscribe(deviceFeatures => {
			const fea = deviceFeatures.find(fe => fe.Name === 'password');
			if (fea !== undefined) {
				this.passwordSupport = true;
			}
		});
		this.isTechnicianSub = this.isTechnician$.subscribe((isTechnician => {
			this.isTechnician = isTechnician;
		}));
		if (this.isMTMAConnect()) {
			this.mtmaModulesSub = this.mtmaModules$.subscribe(list => {
				this.mtmAc = list.filter(item => item.Type === 0)[0];
			});
			this.mtmaChangesSub = this.mtmaChanges$.subscribe(mtmaChanges => {
				this.mtmaChanges = mtmaChanges;
				this.updateMenu();
			});
			this.mtmaActiveTaskSub = this.mtmaActiveTask$.subscribe(mtmaActiveTask => {
				this.mtmaActiveTask = mtmaActiveTask;
				if (mtmaActiveTask !== null && mtmaActiveTask.Id !== '') {
					this.progress = mtmaActiveTask.Progress.toString() + ' %';
				} else {
					this.progress = undefined;
				}
				this.updateMenu();
			});
			if (this.device.CurrentlyOwned) {
				this.getUpdateMtma();
				this.startPolling();
			}
		}
	}

	ngOnDestroy(): void {
		this.polling?.unsubscribe();
		this.deviceSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.deviceModelSub?.unsubscribe();
		this.deviceInfoSub?.unsubscribe();
		this.siteSub?.unsubscribe();
		this.tasksSub?.unsubscribe();
		this.impersModeSub?.unsubscribe();
		this.mtmaModulesSub?.unsubscribe();
		this.mtmaChangesSub?.unsubscribe();
		this.mtmaActiveTaskSub?.unsubscribe();
		this.deviceFeatureSub?.unsubscribe();
		this.isTechnicianSub?.unsubscribe();
		this.taskPollingService.stopPollingExit();
	}

	updateMenu(): void {
		this.menuitems = [
			{
				label: this.getTextMtmaWriteConfig(), id: 'buttonMTMACloud', icon: 'pi ' + this.getIconMtmaWriteConfig(),
				command: () => this.writeConfig(), visible: this.isMTMAConnect(),
				disabled: this.isDeviceUnavailable() || this.onFirmwareUpdate(),
			},
			{
				label: this.translate.instant('BACK_TO_DEVICES'), id: 'buttonDeviceListView', icon: 'pi cm-arrow-left',
				command: () => this.toDeviceList(),
			},
			{
				label: this.translate.instant('ADD_DEVICE'), id: 'buttonDeviceAdd', icon: 'pi cm-plus',
				command: () => this.addDevice(), disabled: !this.canAddChild(), visible: this.supportAddChildren() && this.device.CurrentlyOwned,
			},
			{
				label: this.translate.instant('EVENTS'), id: 'buttonDeviceNotification', icon: 'pi cm-bell',
				command: () => this.toEventsList(), visible: this.device.CurrentlyOwned,
			},
			{
				label: this.translate.instant('TASKS'), id: 'buttonDeviceTask', icon: 'pi cm-cloud-progress',
				command: () => this.tasks(), visible: this.hasTasks()
			},
			{
				label: this.translate.instant('SHARE_DEVICE'), id: 'buttonDeviceShare', icon: 'pi cm-share',
				command: () => this.share(), visible: this.sharable() && this.device.CurrentlyOwned, disabled: !this.configured(),
			},
			/*{
				label: this.translate.instant('EDIT_PASSWORD'), id: 'buttonPasswordEdit', icon: 'pi cm-key',
				command: () => this.editPassword(),
			},*/
			{
				label: this.translate.instant('EDIT_DEVICE'), id: 'buttonDeviceEdit', icon: 'pi cm-pencil',
				command: () => this.editDevice(),
			},
			{
				label: this.translate.instant('DELETE_DEVICE'), id: 'buttonDeviceDelete', icon: 'pi cm-trash',
				command: () => this.delete(), visible: this.device.CurrentlyOwned, disabled: this.isIOMtma(),
			},
			{
				label: this.translate.instant('DELETE_DEVICE'), id: 'buttonDeviceDeleteShare', icon: 'pi cm-trash',
				command: () => this.removeShare(), visible: this.canRemoveShare(),
			},
		];
	}

	hasTasks(): boolean {
		return supportTasks(this.device);
	}

	isMTMAConnect(): boolean {
		if (this.device === undefined) {
			return false;
		}
		return isMtmacModel(this.device.ModelId);
	}

	startPolling(): void {
		this.polling = interval(5 * 1000).subscribe(() => this.getUpdateMtma());
	}

	getUpdateMtma(): void {
		this.store.dispatch(DeviceActions.getMtmaChanges());
		this.store.dispatch(DeviceActions.getMtmaActiveTask());
		this.store.dispatch(DeviceActions.updateCurrentDeviceStatus());
	}

	onFirmwareUpdate(): boolean {
		return this.deviceStatus.FwUpdate === CLOUD_FW_UPGRADE_STATE.CLOUD_FW_UPGRADE_STATE_STARTED ||
			this.deviceStatus.FwUpdate === CLOUD_FW_UPGRADE_STATE.CLOUD_FW_UPGRADE_STATE_UPDATING;
	}

	toDeviceList() {
		this.router.navigate(['../'], { relativeTo: this.route });
	}

	toEventsList() {
		//this.router.navigate(['/home/events'], { queryParams: { deviceId: this.device.Id } });// Error
		this.router.navigate(['../../events'], { relativeTo: this.route, queryParams: { deviceId: this.device.Id } });
	}

	isThermoDeviceOrThOneGw(): boolean {
		return this.device.CategoryId === EDeviceCategory.Thermostat || isThOneGw(this.device.ModelId);
	}

	sharable(): boolean {
		if (this.isThermoDeviceOrThOneGw()) {
			return false;
		}
		return this.deviceModel !== undefined && this.deviceModel.Sharable !== undefined &&
			this.deviceModel.Sharable;
	}

	configured(): boolean {
		return this.deviceStatus.Configured === 0;
	}

	supportAddChildren(): boolean {
		return this.deviceModel !== undefined && (this.deviceModel.CategoryId === EDeviceCategory.Gateway ||
			this.deviceModel.CategoryId === EDeviceCategory.SubGateway);
	}

	canAddChild(): boolean {
		return this.configured() && this.deviceStatus.Online && !isAutomFwUpdateInProgress(this.deviceStatus) && (this.deviceInfo !== undefined && this.deviceModel !== undefined && (
			this.deviceModel.MaxSubGateways > this.childSubGatewaysCount ||
			this.deviceModel.MaxAutomations > this.childAutomationsCount ||
			(this.deviceModel.LocalInputs !== undefined && this.deviceModel.LocalInputs > 0 && this.deviceInfo.AllowedInputs?.length > 0) ||
			(this.deviceModel.LocalOutputs !== undefined && this.deviceModel.LocalOutputs > 0 && this.deviceInfo.AllowedOutputs?.length > 0) ||
			(this.deviceModel.BoardInputs !== undefined && this.deviceModel.BoardInputs > 0) ||
			(this.deviceModel.BoardOutputs !== undefined && this.deviceModel.BoardOutputs > 0)
		));
	}

	canRemoveShare(): boolean {
		return this.sharable() && !this.device.CurrentlyOwned && !this.impersMode;
	}

	isIOMtma(): boolean {
		if (this.device.ModelId === EDeviceModel.MultiIO && this.device.ParentId !== undefined && this.device.ParentId !== null && this.currentSite !== undefined &&
			this.currentSite.Devices !== undefined && this.currentSite.Devices !== null && this.currentSite.Devices.length > 0) {
			const parentDeviceList = this.currentSite.Devices.filter(device => device.Id === this.device.ParentId);
			if (parentDeviceList.length > 0) {
				return isMtmacModel(parentDeviceList[0].ModelId)
			}
		}
		return false;
	}

	getIconMtmaWriteConfig(): string {
		if (this.mtmaActiveTask !== undefined && this.mtmaActiveTask.Id !== '') {
			return 'cm-cloud-sync';
		} else if (this.mtmaChanges) {
			return 'cm-cloud-arrow-down';
		}
		return 'cm-cloud-synchronized';
	}

	getColorMtmaWriteConfig(): string {
		if (this.mtmaActiveTask !== undefined && this.mtmaActiveTask.Id !== '') {
			return 'danger';
		} else if (this.mtmaChanges) {
			return 'warning';
		}
		return 'success';
	}

	getTextMtmaWriteConfig(): string {
		if (this.mtmaActiveTask !== undefined && this.mtmaActiveTask.Id !== '') {
			return this.translate.instant('ABORT_WRITE_MTMA');
		} else if (this.mtmaChanges) {
			return this.translate.instant('WRITE_MTMA');
		}
		return this.translate.instant('WRITE_MTMA_COMPLETE');
	}

	writeConfig(): void {
		const splitted = this.mtmAc.FWVersion.split('.');
		const ver1 = Number(splitted[0]);
		const ver2 = Number(splitted[1]);
		const ver3 = Number(splitted[2]);
		// minLimitVersion for MTMA STD = 1.1.8
		if (isMtmacStandardModel(this.device.ModelId) && (
			(ver1 < this.minLimitVersion[0]) || (ver1 === this.minLimitVersion[0] && ver2 < this.minLimitVersion[1]) ||
			(ver1 === this.minLimitVersion[0] && ver2 === this.minLimitVersion[1] && ver3 < this.minLimitVersion[2])
		)) {
			this.mtmaUpdateFirmware();
		} else if (this.mtmaActiveTask !== undefined && this.mtmaActiveTask.Id !== '') {
			this.mtmaAbort();
		} else {
			this.mtmaWrite();
		}
	}

	mtmaUpdateFirmware(): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('UPDATE_FIRMWARE_MTMA'),
			message: this.translate.instant('UPDATE_FIRMWARE_MTMA_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.ref = this.dialogService.open(UpdateFirmwareDialogComponent, {
					header: this.translate.instant('UPDATE_FIRWARE_MTMA'),
					// resizable: true,
					contentStyle: {
						overflow: 'auto'
					},
				});
			},
			reject: () => {
			}
		});
	}

	mtmaAbort(): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('ABORT_WRITE_MTMA'),
			message: this.translate.instant('ABORT_WRITE_MTMA_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.getMtmaAbort({ taskId: this.mtmaActiveTask.Id }));
			},
			reject: () => {
			}
		});
	}

	mtmaWrite(): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('WRITE_MTMA'),
			message: this.translate.instant('WRITE_MTMA_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.mtmaWriteConfig());
			},
			reject: () => {
			}
		});
	}

	offline(): boolean {
		return !this.deviceStatus.Online;
	}

	isDeviceUnavailable(): boolean {
		return isDeviceUnavailable(this.deviceStatus);
	}

	addDevice(): void {
		this.store.dispatch(DeviceActions.deviceDialogReset());
		this.ref = this.dialogService.open(AddAutomationDialogComponent, {
			header: this.translate.instant('ADD') + ' ' + this.translate.instant('DEVICE') + ' / ' + this.translate.instant('SUB_GATEWAY_NEW'),
			width: '70vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				parentDevice: this.device,
				parentModel: this.deviceModel,
				parentInfo: this.deviceInfo,
				automationsCount: this.childAutomationsCount,
				subGatewaysCount: this.childSubGatewaysCount,
			},
		});
	}

	editPassword(): void {
		this.store.dispatch(DeviceActions.passwordChangeReset());
		this.ref = this.dialogService.open(PasswordEditDialogComponent, {
			header: this.translate.instant('EDIT_PASSWORD'),
			width: '70vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				device: this.device
			},
		});
	}

	editDevice(): void {
		this.store.dispatch(DeviceActions.deviceDialogReset());
		this.ref = this.dialogService.open(EditDeviceDialogComponent, {
			header: this.translate.instant('EDIT_DEVICE'),
			width: '70vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				device: this.device
			},
		});
	}

	share(): void {
		this.store.dispatch(DeviceActions.resetShareDialog());
		this.store.dispatch(InvitationsActions.resetInvitationsShareDialog());
		this.ref = this.dialogService.open(ShareDeviceDialogComponent, {
			header: this.translate.instant('SHARE_DEVICE') + ': ' + this.device.Name,
			width: '70vw',
			breakpoints: { '1600px': '95vw' },
			contentStyle: { overflow: 'auto' },
			data: {},
		});
	}

	delete(): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		let message = this.translate.instant('DELETE_DEVICE_WARNING');
		if (this.isMTMAConnect()) {
			message = this.translate.instant('DELETE_DEVICE_MTMA_WARNING');
		}
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_DEVICE'),
			message: message,
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.deleteDevice({ deviceId: this.device.Id }));
				this.navigateParent(this.device.ParentId);
			},
			reject: () => {
			}
		});
	}

	navigateParent(parentId: number): void {
		if (parentId) {
			this.router.navigate(['..'], { relativeTo: this.route }).then((res) => {
				if (!res) {
					window.location.reload();
				}
			}).catch(() => {
				window.location.reload();
			});
		} else {
			this.router.navigate(['..'], { relativeTo: this.route }).then((res) => {
				if (!res) {
					window.location.reload();
				}
			});
		}
	}

	tasks(): void {
		this.ref = this.dialogService.open(TasksDialogComponent, {
			header: this.translate.instant('TASKS'),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	removeShare(): void {
		this.store.dispatch(DeviceActions.deletingSharedDevice());
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_SHARED_DEVICE'),
			message: this.translate.instant('DELETE_SHARED_DEVICE_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.deleteSharedDevice({ deviceId: this.device.Id }));
				this.shareDeleteSub = this.store.select('device', 'shareDeleted')
					.pipe(filter(deleted => !!deleted))
					.subscribe(() => {
						if (this.currentSite.DevicesTree.Children.length === 1) {
							this.store.dispatch(SiteActions.getSites({}));
							this.store.dispatch(DeviceVctActions.getSitesVctList());
							this.store.dispatch(SiteActions.getSitesOnlineDevices({}));
							this.router.navigate(['../../sites'], { relativeTo: this.route });
						} else {
							this.store.dispatch(SiteActions.getSite({}));
							this.router.navigate(['..']);
						}
					});
			},
			reject: () => {
			}
		});
	}

	// Design
	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	underTablet(): boolean {
		return StandardPageLayout.underTablet(this.screenWidth);
	}

	getAddLabel(): string {
		if (this.screenWidth <= 760) {
			return "";
		} else if (this.screenWidth <= StandardDesignParameters.FOOTER_EXTENDED_MIN_WIDTH) {
			return this.translate.instant("ADD");
		} else {
			return this.translate.instant("ADD_DEVICE_SLAVE_NEW");
		}
	}
}
